import { Link } from "react-router-dom";
import {
    Lightning,
    ArrowLeft,
    ArrowRight,
    CheckCircle,
    Books,
    Brain,
    Rocket,
    PaintBrush,
    Megaphone,
    Trophy,
    ChartLineUp,
    FlowArrow,
    Crown,
    Clock,
    Target,
    Wrench,
    Lightbulb,
    PlayCircle,
} from "@phosphor-icons/react";

/**
 * Operator's Operations Guide — public page that doubles as:
 *  (a) Pre-purchase confidence builder ("here's exactly how the system runs")
 *  (b) Post-purchase Day-1 reference for new members
 *
 * Linked from Landing CTA, FAQ page, and dashboard onboarding tab.
 */

const FIRST_90_MINUTES = [
    {
        time: "0–10 min",
        title: "Pick your Recipe",
        body: "Open the Recipe Vault. Don't analyze — pick the FIRST one that catches your gut. Every Recipe in the Vault is engineered for a real, monetizable niche. Sarah picked Recipe #001 in 4 minutes. Bailey picked Recipe #005 in 2 minutes. Speed > deliberation.",
    },
    {
        time: "10–25 min",
        title: "Generate your script",
        body: "Read the Recipe's 'Topic Bank' section. Pick ONE topic. Drop it into the Nexus Script Engine. Hit GENERATE. You'll have a fully-structured 60-second script in under 10 seconds — hooks, story beats, B-roll cues, voice-over notes, viral CTA.",
    },
    {
        time: "25–40 min",
        title: "Engineer your headline + thumbnail",
        body: "Open the Visual Authority Guide. Pick ONE Mad-Lib headline formula (Forbidden Knowledge / Personal Transformation / FOMO). Build your thumbnail using the Rule of Two color framework. Don't perfect — pattern-match to what's in the guide.",
    },
    {
        time: "40–70 min",
        title: "Record voice-over + assemble visuals",
        body: "Use ElevenLabs free tier (or Capcut's built-in TTS) for voice-over. Pull stock B-roll from Pexels/Unsplash (free, the Recipe usually links to good ones). Stitch in Capcut. Don't over-edit — the algorithm rewards content, not production.",
    },
    {
        time: "70–85 min",
        title: "Deploy to your platform",
        body: "Open the Deployment Hub. Open the playbook for your platform (TikTok / YouTube / Instagram). Follow the checklist exactly — caption format, hashtag count, posting time, description structure.",
    },
    {
        time: "85–90 min",
        title: "Drop a note in the Feedback Lounge",
        body: "Post a short note: 'First video up — Recipe #N — feedback welcome.' Earns you 10 leaderboard points + tactical feedback from other Operators within hours.",
    },
];

const TOOLS_DEEP = [
    {
        icon: Books,
        name: "Recipe Vault",
        what: "12 niche blueprints, each engineered around a real, profitable channel archetype.",
        how: "Each Recipe contains: niche positioning, audience psychology, hook formulas, monetization paths, 30-topic Topic Bank, content cadence, and 3-month roadmap. Pick ONE. Run it for 60 days minimum before switching.",
        pro: "Don't pick the 'most interesting' Recipe. Pick the one whose Topic Bank you could write 30 videos on without research. Niche-fit > niche-excitement.",
    },
    {
        icon: Brain,
        name: "Nexus Script Engine",
        what: "Gemini 3 Flash AI fine-tuned for short-form viral scripts.",
        how: "Drop a topic in. Pick a tone. Hit GENERATE. Output: hook (≤3 sec), story arc, B-roll cues, voice-over notes, viral CTA. Edit lightly — don't rewrite. The engine knows what works.",
        pro: "Run the same topic 3 times with different tones. Pick the script that makes YOU want to record it. Energy in = energy out.",
    },
    {
        icon: PaintBrush,
        name: "Visual Authority Guide",
        what: "The headline + thumbnail psychology bible.",
        how: "Mad-Lib headline swipe file (3 categories), Vegas-Tech thumbnail framework (Extreme Emotion / Rule of Two / Color Signal), 2026 SEO Signature, Deployment Checklist. Apply BEFORE you record — your thumbnail decides whether the algorithm gives you a chance.",
        pro: "If videos aren't getting traction, audit thumbnails BEFORE you blame content. 8/10 dead videos die at the thumbnail.",
    },
    {
        icon: Rocket,
        name: "Deployment Hub",
        what: "Platform-specific launch playbooks for TikTok, YouTube, Instagram.",
        how: "Each playbook: 10K follower formula, algorithm-friendly upload checklist, monetization unlock requirements, growth tactics specific to that platform's 2026 algo.",
        pro: "Don't post on all 3 platforms day 1. Pick ONE. Master its playbook. Add platform 2 at Month 2. Adding platforms is a Month-3 problem.",
    },
    {
        icon: Megaphone,
        name: "Growth Playbook",
        what: "Marketing & engagement bibles. Drop-in tactics, not theory.",
        how: "Creator Launchpad Marketing Guide (audience-build framework), Best Times to Post 2026 (Global Edition), Engagement Hooks That Double Your Comments. Read once, then USE.",
        pro: "The Engagement Hooks Playbook alone will 2x your comment count, which the algorithm reads as 'this is making conversations'. Free leverage.",
    },
    {
        icon: ChartLineUp,
        name: "Alpha Feed",
        what: "Curated weekly intel on what's exploding RIGHT now.",
        how: "Algorithm shifts. New monetization opportunities. Niche openings before saturation. Read it every Monday morning.",
        pro: "When the Alpha Feed flags a niche opening, you have ~30 days before it's saturated. Move fast.",
    },
    {
        icon: Trophy,
        name: "Leaderboard + Feedback Lounge",
        what: "Live ranking + Operator-to-Operator support network.",
        how: "Climb by posting wins. Drop blockers in the Lounge — usually answered within hours by other Operators. The first 10 members to post earn a personal channel audit from Steve.",
        pro: "Post your blockers, not your wins. Wins feel good but blockers get solved. The Lounge is a war room, not a victory lap.",
    },
    {
        icon: FlowArrow,
        name: "Prompt Lab",
        what: "Battle-tested AI prompt library — copy-paste ready.",
        how: "Hooks, captions, B-roll briefs, comment replies, DM funnels. Already engineered, already A/B tested.",
        pro: "Save your favorite 3-5 prompts to your phone notes. You'll use them daily.",
    },
];

const CADENCE = [
    { day: "Mon", task: "Read Alpha Feed (5 min) · plan week's 3-5 topics" },
    { day: "Tue", task: "Generate scripts in batch (Script Engine, 30 min)" },
    { day: "Wed", task: "Record + edit videos in batch (90 min)" },
    { day: "Thu", task: "Schedule deployments (Deployment Hub checklist)" },
    { day: "Fri", task: "Engagement: reply to comments, DM funnels active" },
    { day: "Sat", task: "Audit metrics — top performer, bottom performer, what changed" },
    { day: "Sun", task: "Drop wins/blockers in Feedback Lounge · refine for next week" },
];

const COMMON_MISTAKES = [
    {
        mistake: "Niche-hopping",
        fix: "Pick ONE Recipe. Run it for 60 days minimum. Algorithms reward consistency more than 'finding the right niche'.",
    },
    {
        mistake: "Over-producing",
        fix: "The algorithm rewards 'good enough + fast' over 'perfect + slow'. Capcut + ElevenLabs + Pexels is enough. Don't buy gear until you've made $500.",
    },
    {
        mistake: "Skipping the thumbnail audit",
        fix: "Thumbnails decide whether your video gets a chance. If you're not following the Visual Authority Guide framework, you're hand-tying the algorithm.",
    },
    {
        mistake: "Posting once a week",
        fix: "Most successful Operators post 3-5 times per week in Month 1. Volume builds the algorithm's confidence in your channel. Not 7+ daily — that burns out and tanks quality.",
    },
    {
        mistake: "Not engaging with comments",
        fix: "Reply to your first 50 comments on every video. The algorithm reads engagement velocity in the first 60 minutes. Your replies count toward velocity.",
    },
];

const STACK = [
    { tool: "Capcut (Free)", role: "Video editing + built-in TTS", url: "https://www.capcut.com" },
    { tool: "ElevenLabs (Free tier)", role: "AI voice-over (premium tier optional)", url: "https://elevenlabs.io" },
    { tool: "Pexels", role: "Free stock B-roll + photos", url: "https://www.pexels.com" },
    { tool: "Unsplash", role: "Free stock images", url: "https://unsplash.com" },
    { tool: "TikTok / YouTube / Instagram", role: "Distribution channels", url: null },
    { tool: "ManyChat (Free tier)", role: "Auto-DM funnels for IG (covered in Deployment Hub)", url: "https://manychat.com" },
];

export default function Operations() {
    return (
        <div className="min-h-screen bg-background relative">
            {/* Header */}
            <header className="border-b border-white/10 px-6 md:px-12 py-4 flex justify-between items-center">
                <Link to="/" data-testid="ops-logo" className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={18} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-lg">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </Link>
                <Link
                    to="/"
                    data-testid="ops-back-link"
                    className="label-mono text-xs hover:text-electric-blue flex items-center gap-1.5 transition-colors"
                >
                    <ArrowLeft size={12} weight="bold" /> BACK
                </Link>
            </header>

            {/* HERO */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
                <div className="relative">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-electric-blue/40 bg-electric-blue/10 mb-6">
                        <Wrench size={14} weight="fill" className="text-electric-blue" />
                        <span className="label-mono text-[10px] text-electric-blue">OPERATOR'S MANUAL · HOW TO RUN THE SYSTEM</span>
                    </div>
                    <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[1.02] mb-6">
                        The Operations Guide.<br />
                        <span className="text-electric-blue neon-text">From signup to first viral video.</span>
                    </h1>
                    <p className="text-lg md:text-xl text-foreground/80 leading-relaxed max-w-3xl mb-8">
                        This is the no-fluff walkthrough of how every tool in Nexus.Lab fits together,
                        what to do in your first 90 minutes, the weekly cadence that compounds, and
                        the mistakes that kill new Operators in Week 1.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Link
                            to="/dashboard"
                            data-testid="ops-hero-dashboard-cta"
                            className="inline-flex items-center justify-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)]"
                        >
                            <PlayCircle size={18} weight="fill" />
                            ENTER THE LAB
                            <ArrowRight size={18} weight="bold" />
                        </Link>
                        <Link
                            to="/faq"
                            data-testid="ops-hero-faq-cta"
                            className="inline-flex items-center justify-center gap-2 border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-8 py-4 text-sm tracking-wider uppercase transition-all"
                        >
                            FAQ + CASE STUDIES
                        </Link>
                    </div>
                </div>
            </section>

            {/* FIRST 90 MINUTES */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-16">
                <div className="mb-10">
                    <p className="label-mono text-electric-blue mb-3">PART 1 · YOUR FIRST 90 MINUTES</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        Don't read the whole platform.<br />
                        <span className="text-electric-blue">Run this script tonight.</span>
                    </h2>
                    <p className="text-foreground/75 max-w-2xl">
                        80% of new Operators fail because they spend Day 1 reading every Recipe instead of
                        executing one. Don't be 80%. Here's exactly what to do, hour by hour.
                    </p>
                </div>

                <div className="space-y-3">
                    {FIRST_90_MINUTES.map((step, idx) => (
                        <div
                            key={step.time + step.title}
                            data-testid={`first-90-step-${idx}`}
                            className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue/40 transition-all p-5 md:p-6"
                        >
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-electric-blue/10 border border-electric-blue/30 flex items-center justify-center">
                                    <span className="font-mono text-xs text-electric-blue font-bold">{String(idx + 1).padStart(2, "0")}</span>
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                        <span className="label-mono text-[10px] text-electric-blue bg-electric-blue/10 px-2 py-0.5 border border-electric-blue/30">
                                            <Clock size={10} weight="bold" className="inline mr-1" />
                                            {step.time}
                                        </span>
                                        <h3 className="font-heading font-black text-lg leading-tight">{step.title}</h3>
                                    </div>
                                    <p className="text-sm text-foreground/80 leading-relaxed">{step.body}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* TOOL DEEP-DIVE */}
            <section className="relative max-w-6xl mx-auto px-6 md:px-12 py-16">
                <div className="mb-10 text-center">
                    <p className="label-mono text-electric-blue mb-3">PART 2 · THE TOOLS, IN DETAIL</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        How each tool works,<br />
                        <span className="text-electric-blue">and the pro-move for each.</span>
                    </h2>
                </div>

                <div className="space-y-4">
                    {TOOLS_DEEP.map((tool, idx) => {
                        const Icon = tool.icon;
                        return (
                            <div
                                key={tool.name}
                                data-testid={`tool-deep-${idx}`}
                                className="bg-[#0A0A0A] border border-white/10 hover:border-electric-blue/30 transition-all p-6 md:p-8"
                            >
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="flex-shrink-0 w-12 h-12 bg-electric-blue/10 border border-electric-blue/30 flex items-center justify-center">
                                        <Icon size={22} weight="fill" className="text-electric-blue" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-heading font-black text-2xl tracking-tight mb-1">{tool.name}</h3>
                                        <p className="text-sm text-foreground/80 italic">{tool.what}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-0 md:ml-16">
                                    <div>
                                        <p className="label-mono text-[10px] text-muted-foreground mb-2">HOW TO USE IT</p>
                                        <p className="text-sm text-foreground/85 leading-relaxed">{tool.how}</p>
                                    </div>
                                    <div className="bg-electric-blue/5 border-l-2 border-electric-blue p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Lightbulb size={14} weight="fill" className="text-electric-blue" />
                                            <p className="label-mono text-[10px] text-electric-blue">PRO MOVE</p>
                                        </div>
                                        <p className="text-sm text-foreground/85 leading-relaxed">{tool.pro}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* WEEKLY CADENCE */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-16">
                <div className="mb-10">
                    <p className="label-mono text-electric-blue mb-3">PART 3 · THE WEEKLY CADENCE</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        7 days. <span className="text-electric-blue">7 leverage points.</span>
                    </h2>
                    <p className="text-foreground/75 max-w-2xl">
                        Once you're past your first video, this is the rhythm that compounds. Each day = one specific high-leverage task.
                    </p>
                </div>

                <div className="bg-[#0A0A0A] border border-white/10 overflow-hidden">
                    {CADENCE.map((row, idx) => (
                        <div
                            key={row.day}
                            data-testid={`cadence-${row.day.toLowerCase()}`}
                            className={`flex items-center gap-4 md:gap-6 px-5 md:px-8 py-4 ${idx < CADENCE.length - 1 ? "border-b border-white/5" : ""} hover:bg-electric-blue/5 transition-colors`}
                        >
                            <div className="flex-shrink-0 w-14 md:w-16 font-mono text-electric-blue text-sm md:text-base font-bold">{row.day}</div>
                            <div className="flex-1 text-sm md:text-base text-foreground/85">{row.task}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* COMMON MISTAKES */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-16">
                <div className="mb-10">
                    <p className="label-mono text-electric-blue mb-3">PART 4 · WHAT KILLS NEW OPERATORS</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        5 mistakes.<br />
                        <span className="text-electric-blue">Don't make any of them.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {COMMON_MISTAKES.map((m, idx) => (
                        <div key={m.mistake} data-testid={`mistake-${idx}`} className="bg-[#0A0A0A] border border-white/10 p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <Target size={14} weight="fill" className="text-red-400" />
                                <p className="label-mono text-[10px] text-red-400">MISTAKE</p>
                            </div>
                            <p className="font-heading font-bold text-base mb-3">{m.mistake}</p>
                            <div className="border-t border-white/5 pt-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle size={14} weight="fill" className="text-electric-blue" />
                                    <p className="label-mono text-[10px] text-electric-blue">FIX</p>
                                </div>
                                <p className="text-sm text-foreground/80 leading-relaxed">{m.fix}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* RECOMMENDED STACK */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-16">
                <div className="mb-10">
                    <p className="label-mono text-electric-blue mb-3">PART 5 · THE STACK</p>
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.05] mb-4">
                        $0 to start.<br />
                        <span className="text-electric-blue">Every tool you actually need.</span>
                    </h2>
                </div>

                <div className="bg-[#0A0A0A] border border-white/10">
                    {STACK.map((s, idx) => (
                        <div
                            key={s.tool}
                            data-testid={`stack-${idx}`}
                            className={`flex items-center gap-4 px-5 md:px-6 py-4 ${idx < STACK.length - 1 ? "border-b border-white/5" : ""} hover:bg-white/2 transition-colors`}
                        >
                            <div className="flex-1">
                                <p className="font-heading font-bold text-base mb-1">{s.tool}</p>
                                <p className="text-xs text-muted-foreground">{s.role}</p>
                            </div>
                            {s.url && (
                                <a
                                    href={s.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-electric-blue hover:underline label-mono flex items-center gap-1"
                                >
                                    OPEN <ArrowRight size={10} weight="bold" />
                                </a>
                            )}
                        </div>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground italic mt-4 text-center">
                    Total monthly cost to run the system at scale: $20 (Founder membership) + $0 in tools = $20/mo. Add gear/upgrades only after your first $500.
                </p>
            </section>

            {/* CTA */}
            <section className="relative max-w-5xl mx-auto px-6 md:px-12 py-20">
                <div className="border-2 border-electric-blue bg-[#0A0A0A] p-8 md:p-12 relative overflow-hidden neon-border">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-electric-blue to-transparent" />
                    <div className="absolute -top-12 -right-12 w-64 h-64 bg-electric-blue/15 rounded-full blur-3xl pointer-events-none" />
                    <div className="relative">
                        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter leading-[1.02] mb-4">
                            You've read the manual.<br />
                            <span className="text-electric-blue neon-text">Now run the system.</span>
                        </h2>
                        <p className="text-foreground/80 max-w-2xl mb-6">
                            Reading is leverage. Execution is income. The Operators who post their first video within 48 hours of joining are 4-7x more likely to hit their first profitable month.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/signup"
                                data-testid="ops-bottom-claim-cta"
                                className="inline-flex items-center justify-center gap-2 bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-8 py-4 text-sm tracking-wider uppercase transition-all hover:shadow-[0_0_32px_rgba(0,123,255,0.7)] animate-glow-pulse"
                            >
                                <Crown size={18} weight="fill" />
                                CLAIM A FOUNDER SEAT
                                <ArrowRight size={18} weight="bold" />
                            </Link>
                            <Link
                                to="/dashboard"
                                data-testid="ops-bottom-dashboard-cta"
                                className="inline-flex items-center justify-center gap-2 border border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-8 py-4 text-sm tracking-wider uppercase transition-all"
                            >
                                ALREADY A MEMBER · ENTER LAB
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="border-t border-white/10 px-6 md:px-12 py-8 max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
                    <p>© 2026 Nexus Creative Group LLC · Built for faceless operators.</p>
                    <div className="flex items-center gap-6">
                        <Link to="/faq" className="hover:text-electric-blue transition-colors">FAQ</Link>
                        <Link to="/terms" className="hover:text-electric-blue transition-colors">Terms</Link>
                        <Link to="/privacy" className="hover:text-electric-blue transition-colors">Privacy</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
